import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Clock, MessageCircle, Mail, CheckCircle, Send } from 'lucide-react';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

export default function Contact() {
  useScrollAnimation();

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', service: '', county: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormData({ name: '', phone: '', email: '', service: '', county: '', message: '' });
  };

  return (
    <div>
      {/* PAGE HEADER */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-green-400 font-bold text-sm uppercase tracking-widest">Get In Touch</span>
          <h1 className="text-5xl font-black text-white mt-3 mb-4">Contact Us</h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto">
            Ready to get rid of that rubbish? Get a free, no-obligation quote from Chris today. We respond fast!
          </p>
        </div>
      </section>

      {/* QUICK CONTACT CARDS */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a
              href="tel:0852730131"
              className="group bg-green-600 hover:bg-green-700 rounded-2xl p-6 text-white transition-all duration-200 card-hover animate-on-scroll"
            >
              <Phone size={28} className="mb-3" />
              <div className="font-bold text-lg mb-1">Call Us</div>
              <div className="text-green-100 text-sm">085 273 0131</div>
              <div className="text-green-200 text-xs mt-1">+353 85 273 0131</div>
            </a>

            <a
              href="https://wa.me/353852730131"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-emerald-500 hover:bg-emerald-600 rounded-2xl p-6 text-white transition-all duration-200 card-hover animate-on-scroll"
            >
              <MessageCircle size={28} className="mb-3" />
              <div className="font-bold text-lg mb-1">WhatsApp</div>
              <div className="text-emerald-100 text-sm">Message on WhatsApp</div>
              <div className="text-emerald-200 text-xs mt-1">Quick response guaranteed</div>
            </a>

            <a
              href="mailto:info@lawlorrubbish.ie"
              className="group bg-gray-800 hover:bg-gray-900 rounded-2xl p-6 text-white transition-all duration-200 card-hover animate-on-scroll"
            >
              <Mail size={28} className="mb-3" />
              <div className="font-bold text-lg mb-1">Email Us</div>
              <div className="text-gray-300 text-sm">info@lawlorrubbish.ie</div>
              <div className="text-gray-400 text-xs mt-1">We reply within hours</div>
            </a>

            <div className="bg-green-50 rounded-2xl p-6 border border-green-100 animate-on-scroll">
              <Clock size={28} className="text-green-600 mb-3" />
              <div className="font-bold text-gray-900 text-lg mb-1">Opening Hours</div>
              <div className="text-gray-600 text-sm">Mon – Sun: 7am – 8pm</div>
              <div className="text-green-600 text-xs font-bold mt-1">Same Day Service Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">

            {/* FORM */}
            <div className="animate-on-scroll">
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="text-2xl font-black text-gray-900 mb-2">Request a Free Quote</h2>
                <p className="text-gray-500 text-sm mb-8">Fill in the form and Chris will get back to you quickly with a fair price.</p>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-500 mb-6">Thanks for getting in touch. Chris will contact you shortly with your free quote.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-green-600 font-semibold hover:underline text-sm"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="085 000 0000"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Service Needed *</label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          required
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                        >
                          <option value="">Select a service</option>
                          <option>General Rubbish Removal</option>
                          <option>Property Clearance</option>
                          <option>Furniture Removal</option>
                          <option>Appliance Removal</option>
                          <option>Estate Clean Out</option>
                          <option>Hoarder Clear Out</option>
                          <option>Commercial Rubbish Removal</option>
                          <option>Light Demolition</option>
                          <option>Shed Removal</option>
                          <option>Hot Tub Removal</option>
                          <option>Garden Waste</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your County *</label>
                        <select
                          name="county"
                          value={formData.county}
                          onChange={handleChange}
                          required
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                        >
                          <option value="">Select county</option>
                          <option>Dublin</option>
                          <option>Meath</option>
                          <option>Louth</option>
                          <option>Kildare</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Message / Job Description *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Describe what needs to be removed, rough quantity, any access details..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 text-base"
                    >
                      <Send size={18} />
                      Send My Free Quote Request
                    </button>

                    <p className="text-center text-gray-400 text-xs">
                      Or call directly: <a href="tel:0852730131" className="text-green-600 font-bold hover:underline">085 273 0131</a>
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* CONTACT INFO */}
            <div className="space-y-6 animate-on-scroll">
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Get in Touch Directly</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Chris is available 7 days a week and loves to help. Don't hesitate to call — same day service is often available!
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <Phone size={18} className="text-green-600" /> Phone & WhatsApp
                </h3>
                <a
                  href="tel:0852730131"
                  className="block text-2xl font-black text-green-600 hover:text-green-700 mb-1 transition-colors"
                >
                  085 273 0131
                </a>
                <p className="text-gray-500 text-sm">+353 85 273 0131 (International)</p>
                <a
                  href="https://wa.me/353852730131"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm"
                >
                  <MessageCircle size={16} />
                  Message on WhatsApp
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-green-600" /> Hours of Operation
                </h3>
                <div className="space-y-2">
                  {[
                    { day: 'Monday – Friday', hours: '7:00 AM – 8:00 PM' },
                    { day: 'Saturday', hours: '7:00 AM – 8:00 PM' },
                    { day: 'Sunday', hours: '8:00 AM – 6:00 PM' },
                  ].map(({ day, hours }) => (
                    <div key={day} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">{day}</span>
                      <span className="text-gray-900 font-bold">{hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-green-50 rounded-xl p-3 border border-green-100">
                  <p className="text-green-700 text-xs font-semibold flex items-center gap-2">
                    <CheckCircle size={14} />
                    Same Day Service Available — Call Early to Secure Your Slot!
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <MapPin size={18} className="text-green-600" /> Areas We Cover
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Dublin City', 'North Dublin', 'South Dublin', 'West Dublin',
                    'Co. Meath', 'Co. Louth', 'Co. Kildare', 'Fingal',
                    'Drogheda', 'Navan', 'Dundalk', 'Naas',
                  ].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                      {area}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-600 rounded-2xl p-6 text-white">
                <h3 className="font-black text-lg mb-2">No Job Too Big or Small!</h3>
                <p className="text-green-100 text-sm leading-relaxed mb-4">
                  From a single bag of rubbish to a full estate clearance — Chris handles it all with the same care and professionalism.
                </p>
                <div className="flex flex-col gap-2">
                  {['Free Estimates', 'No Hidden Fees', 'Fully Licensed & Insured', 'Eco-Friendly Disposal'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-green-100 text-sm">
                      <CheckCircle size={14} className="text-green-300" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP PLACEHOLDER */}
      <section className="py-0">
        <div className="bg-gray-200 h-72 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-800/20 to-gray-400/20"></div>
          <div className="text-center z-10">
            <MapPin size={40} className="text-green-700 mx-auto mb-3" />
            <p className="font-bold text-gray-700 text-lg">Serving Dublin, Meath, Louth & Kildare</p>
            <p className="text-gray-500 text-sm">Locally Owned & Operated</p>
            <a
              href="https://maps.google.com/?q=Dublin,+Ireland"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 bg-green-600 text-white font-semibold px-5 py-2 rounded-full text-sm hover:bg-green-700 transition-colors"
            >
              View on Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
